import React, { useMemo } from 'react';
import MuiTabs from '@mui/material/Tabs';
import MuiTab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Divider, Stack, styled } from '@mui/material';
import { TabPanelProps, TabsProps } from './types';

const CustomTabs = styled(MuiTabs)(() => ({
  minHeight: 35,
  margin: '0px 15px',
}));

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
};

function a11yProps(index: number) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

interface IProps extends TabsProps {
  sibling?: React.ReactNode;
}

const FolderTabs: React.FC<IProps> = ({ active = 0, tabItems, orientation, sibling, ...other }) => {
  const [value, setValue] = React.useState(active);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const isVerticalTabs = useMemo(() => orientation === 'vertical', [orientation]);
  const tabIndicatorProps = { style: { display: 'none' } };

  const Tab = useMemo(
    () =>
      styled(MuiTab)(({ theme }) => ({
        ...(isVerticalTabs
          ? {
              padding: '0px',
              fontSize: 12,
              fontWeight: 500,
              minWidth: 80,
              minHeight: 39,
              '&.Mui-selected': {
                backgroundColor: '#456BD91A',
                borderRadius: theme.shape.borderRadius,
                color: theme.palette.common.black,
              },
            }
          : {
              padding: '5px 10px',
              borderRadius: '6px 6px 0px 0px',
              background: '#E2EDFE',
              margin: '15px 2px 0px',
              minWidth: 160,
              minHeight: 29,
              fontSize: 16,
              '&.Mui-selected': {
                color: theme.palette.common.black,
                background: '#FFF',
              },
            }),
        textTransform: 'initial',
        color: theme.palette.primary.main,
        boxShadow: '0px -1px 4px 0px #0000000D',
      })),
    [isVerticalTabs]
  );

  return (
    <Stack direction={isVerticalTabs ? 'row' : 'column'} sx={{ width: '100%' }} gap={isVerticalTabs ? 1 : 0}>
      <Box
        sx={{ backgroundColor: '#f4f4f4' }}
        display="flex"
        justifyContent="space-between"
        alignItems={isVerticalTabs ? 'flex-start' : 'flex-end'}
      >
        <CustomTabs
          value={value}
          onChange={handleChange}
          orientation={orientation}
          {...other}
          TabIndicatorProps={tabIndicatorProps}
        >
          {tabItems.map(({ label, icon, onClick }, index) => (
            <Tab key={label} label={label} icon={icon} {...a11yProps(index)} onClick={() => onClick?.()} />
          ))}
        </CustomTabs>
        {sibling}
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        {tabItems.map(({ content, label }, index) => (
          <TabPanel value={value} index={index} key={label}>
            {content}
          </TabPanel>
        ))}
      </Box>
    </Stack>
  );
};

export default FolderTabs;
